<template>
  <div class="containers">
    <SectionHeader
      :title="$t('meta.routes')"
      @selectProjectUuids="
        projectUuids = $event
        loadData(1)
      "
      :projectSelect="true"
    />

    <div class="content-area">
      <div id="calendar">
        <span class="material-icons">calendar_today</span>
        <span>{{ $t('routes.dateRange') }}:</span>
        <DateRangePicker
          v-model="dateRange"
          @update="loadData(1)"
          opens="right"
          :localeData="localeData"
          :ranges="ranges"
          :append-to-body="true"
        />
      </div>

      <SearchField
        :label="$t('meta.searchForLabel', { entity: $t('meta.route') })"
        :name="'route-search-input'"
        :placeholder="$t('routes.searchPlaceholder')"
        v-model="searchPhrase"
        @input="loadData(1)"
      />

      <LoadingSpinner v-show="loading" />

      <div v-show="!loading">
        <div v-if="routes.length > 0">
          <div v-for="route in routes" :key="route.uuid">
            <div class="area-wrap">
              <a @click="selectRoute(route.uuid)" class="jump-on-map">{{ $t('routes.showOnMap') }}</a>
              <h3>
                <Link :to="`/routes/${route.uuid}`">{{ route.name }}</Link>
              </h3>
              <p>
                <span class="material-icons">delete</span
                ><span>{{ route.count_containers }} {{ $t('routes.containers') }}</span>
              </p>
              <p>
                <span class="material-icons">map</span><span>{{ route.count_stops }} {{ $t('routes.stops') }}</span>
              </p>
              <p>
                <span class="material-icons">schedule</span><span>{{ route.date_formatted }}</span>
              </p>
              <p>
                <span class="material-icons">question_answer</span
                ><span
                  >{{ route.count_comments }} <i v-if="route.count_comments === 1">{{ $t('routes.comment') }}</i
                  ><i v-else>{{ $t('routes.comments') }}</i></span
                >
              </p>
              <p>
                <span class="material-icons">image</span
                ><span
                  >{{ route.count_images }} <i v-if="route.count_images === 1">{{ $t('routes.image') }}</i
                  ><i v-else>{{ $t('routes.images') }}</i></span
                >
              </p>
              <p>
                <span class="material-icons">pin_drop</span
                ><span>{{ route.count_stops_done }}/{{ route.count_stops - 2 }} {{ $t('routes.stopsCompleted') }}</span>
              </p>
              <p v-if="route.company">
                <span class="material-icons">business</span><span>{{ route.company.name }}</span>
              </p>
              <p v-if="route.project">
                <span class="material-icons">all_inbox</span><span>{{ route.project.name }}</span>
              </p>
            </div>
          </div>
          <div v-show="pagination" class="pagination">
            <pagination
              :records="pagination.totalObjects"
              v-model="pagination.page"
              :per-page="25"
              @paginate="loadData"
              :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            ></pagination>
          </div>
        </div>
        <div v-else class="alert alert-danger">{{ $t('routes.noRoutes') }}</div>
      </div>
    </div>
    <div v-show="!cookiesAccepted" class="map-placeholder map-body">
      <h3>{{ $t('meta.acceptCookies') }}</h3>
      <p>{{ $t('meta.acceptCookiesDesc') }}.</p>
      <p class="cta">
        <a @click="acceptCookies" class="button button-green button-small button-round"
          >{{ $t('meta.acceptCookiesCta') }} ›</a
        >
      </p>
    </div>
    <div v-show="cookiesAccepted" id="map-routes" class="map-body"></div>
  </div>
</template>

<script>
import mapbox from 'mapbox-gl'
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'
import Pagination from 'vue-pagination-2'

import { DATEPICKER_LOCALE_DATA, DATEPICKER_RANGES } from '@/views/config'

import SearchField from '@/components/Form/Search/SearchField.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'

import routesApi from '@/api/routes'
import mapUtils from '@/utils/mapbox'
import { mapSettings } from '@/utils/settings'

export default {
  name: 'all_routes',
  components: {
    DateRangePicker,
    LoadingSpinner,
    Pagination,
    SearchField,
    SectionHeader,
  },
  computed: {
    ...mapSettings(['cookiesAccepted']),
    localeData() {
      return DATEPICKER_LOCALE_DATA[this.companyUser.user.language]
    },
    ranges() {
      return DATEPICKER_RANGES[this.companyUser.user.language]
    },
  },
  inject: ['companyUser'],
  data: () => ({
    loading: true,
    map: null,
    dateRange: { startDate: moment().subtract(1, 'months').toDate(), endDate: moment().add(1, 'week').toDate() },
    projectUuids: [],
    searchPhrase: '',
    routes: [],
    routeGeojson: {
      type: 'FeatureCollection',
      features: [],
    },
    pagination: {
      page: 1,
      totalObjects: 0,
    },
    debounced: null,
  }),
  mixins: [mapUtils],
  methods: {
    async loadData(page) {
      this.loading = true

      const params = {
        simple: true,
        page,
        company_uuid: this.companyUser.company_uuid,
        from: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
        to: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
      }

      if (this.projectUuids.length > 0) {
        params['project_uuids'] = this.projectUuids
      }

      if (this.searchPhrase.length > 0) {
        params['search_phrase'] = this.searchPhrase
      }

      const response = await routesApi.getAll(params)
      this.routes = response.routes

      if (this.routes.length === 0) {
        this.emptyMap()
        this.loading = false
      } else {
        this.selectRoute(this.routes[0].uuid)
      }

      this.pagination.page = page
      this.pagination.totalObjects = response.pagination.total_objects

      this.loading = false
    },
    async selectRoute(routeUuuid) {
      const geoJSON = await routesApi.getGeoJson(routeUuuid)
      if (geoJSON) {
        this.routeGeojson = geoJSON
        this.loading = false
        if (this.cookiesAccepted) {
          setTimeout(async () => {
            this.addMap(await routesApi.get(routeUuuid))
          }, 500)
        }
      }
    },
    addMap(route) {
      this.map = null

      mapbox.accessToken = 'pk.eyJ1IjoiYmhlcGJ1cm4iLCJhIjoiY2t6cGw2c2N4M2oxazJubnluMDRmMml2eCJ9.bFHgl2FpFHXN4rx09uOpng'
      this.map = new mapbox.Map({
        container: 'map-routes',
        style: 'mapbox://styles/mapbox/streets-v11',
        center: this.routeGeojson.features[0].geometry.coordinates[0],
        zoom: 10,
      })

      this.map.addControl(new mapbox.FullscreenControl())
      this.map.addControl(new mapbox.NavigationControl())

      var that = this
      this.map.on('load', function () {
        that.map.addSource('lines', {
          type: 'geojson',
          data: that.routeGeojson,
        })

        that.map.addLayer({
          id: 'lines',
          type: 'line',
          source: 'lines',
          paint: {
            'line-width': 3,
            'line-color': ['get', 'color'],
          },
        })

        that.map.addLayer(
          {
            id: 'routearrows',
            type: 'symbol',
            source: 'lines',
            layout: {
              'symbol-placement': 'line',
              'text-field': '▶',
              'text-size': ['interpolate', ['linear'], ['zoom'], 12, 24, 22, 60],
              'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 30, 22, 160],
              'text-keep-upright': false,
            },
            paint: {
              'text-color': ['get', 'color'],
              'text-halo-color': ['get', 'color'],
              'text-halo-width': 3,
            },
          },
          'waterway-label',
        )

        const last_index = (route?.stops?.length || 0) - 1
        route?.stops?.forEach((stop, index) => {
          if (index == 0) {
            new mapbox.Marker(mapUtils.createCustomMarkerFromMaterialIcons('location_searching', '32px'))
              .setLngLat([stop.lng, stop.lat])
              .addTo(that.map)
            return
          } else if (index == last_index) {
            new mapbox.Marker(mapUtils.createCustomMarkerFromMaterialIcons('flag', '22px'))
              .setLngLat([stop.lng, stop.lat])
              .addTo(that.map)
            return
          }

          //var popup = new mapbox.Popup().setText("#"+stop.position).addTo(that.map);
          if (stop.status === 'pending') {
            new mapbox.Marker(mapUtils.createCustomMarkerWithLabel('#FFBA00', stop.position))
              .setLngLat([stop.lng, stop.lat])
              .addTo(that.map)
          } else if (stop.status === 'emptied') {
            new mapbox.Marker(mapUtils.createCustomMarkerWithLabel('#1C9600', stop.position))
              .setLngLat([stop.lng, stop.lat])
              .addTo(that.map)
          } else {
            new mapbox.Marker(mapUtils.createCustomMarkerWithLabel('#FF0000', stop.position))
              .setLngLat([stop.lng, stop.lat])
              .addTo(that.map)
          }
        })
      })
    },
    emptyMap() {
      this.map = null

      this.map = mapUtils.initMap({
        container: 'map-routes',
        center: {
          lng: 7.4639791,
          lat: 51.6163434,
        },
        enableFullscreen: true,
        enableNavigationControls: true,
        zoom: 10,
      })
    },
    acceptCookies() {
      this.cookiesAccepted = true
      if (this.routes.length > 0) {
        this.selectRoute(this.routes[0].uuid)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#calendar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
}
</style>
